import type {
  BalanceTransactionEntry,
  CreditLotEntry,
  V1BillingStatement,
} from '@nadoutong/contracts';
import { useId, useState } from 'react';
import type { FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { PlatformApiError } from '../api/client.ts';
import {
  useBalance,
  useCredits,
  useDisplayRate,
  useRedeemGiftCard,
  useStatements,
  useTransactions,
} from '../api/useBilling.ts';
import { usePromoterBinding } from '../api/usePromoter.ts';
import { CreditPanelBody } from '../components/CreditStatus.tsx';
import { GiftBalanceLine } from '../components/GiftBalanceLine.tsx';
import { PromoterGrantLine } from '../components/PromoterGrantLine.tsx';
import { Modal } from '../components/Modal.tsx';
import { PanelError } from '../components/PanelError.tsx';
import { Skeleton, TableSkeleton } from '../components/Skeleton.tsx';
import { useToast } from '../components/Toast.tsx';
import {
  daysUntil,
  formatCnyCents,
  formatDisplayRateHint,
  formatTimestamp,
  formatUsdMicrosAsCnyDisplay,
  formatUsdMicrosOrCentsAsCnyDisplay,
  formatUsdMicrosOrCentsAsCnyMetered,
  usdMicrosFromCentsFallback,
} from '../components/formatters.ts';
import { ExportExcelButton } from '../export/ExportButton.tsx';
import { exportBilling } from '../export/run.ts';
import { hasPermission, useSession } from '../hooks/useSession.tsx';
import { useT } from '../i18n/index.ts';
import { localizeApiError } from '../api/errorMessages.ts';
import {
  Button,
  Chip,
  EmptyState,
  FormField,
  Input,
  PageHeader,
  Panel,
  TBody,
  Table,
  Td,
  Th,
  THead,
  Tr,
  buttonClass,
  cx,
} from '../ui/index.ts';

/**
 * `/billing` — balance hero, gift-card redeem, and the transaction
 * ledger (epic #950 PR-3, local Field Notes rebuild of the
 * console-shared `createBilling` factory instantiation).
 *
 * Wire behavior preserved: the `org:billing:read` gate renders the
 * forbidden state without firing billing calls; redeem success reports
 * through BOTH the inline status line (`billing-redeem-toast`) and the
 * toast (factory contract); redeem failures render inline and toast;
 * the ledger keeps the `rows.length === PAGE_SIZE` hasMore heuristic
 * and the walk-back running-balance column (first page only).
 */

const PAGE_SIZE = 20;

type TranslateFn = ReturnType<typeof useT>;

export function Billing(): JSX.Element {
  const { user } = useSession();
  if (!hasPermission(user, 'org:billing:read')) {
    return <BillingForbidden />;
  }
  return <BillingContent />;
}

function BillingForbidden(): JSX.Element {
  const t = useT();
  return (
    <section data-testid="billing-page" className="flex flex-col gap-4">
      <PageHeader title={t('billing.title')} sub={t('billing.subtitle')} testId="billing-header" />
      <div data-testid="billing-forbidden" className="rounded-lg border border-line-soft bg-card">
        <EmptyState
          title={t('billing.forbidden.title')}
          hint={t('billing.forbidden.body')}
          action={
            <Link
              to="/dashboard"
              className={buttonClass('secondary')}
              data-testid="billing-forbidden-cta"
            >
              {t('billing.forbidden.cta')}
            </Link>
          }
        />
      </div>
    </section>
  );
}

function BillingContent(): JSX.Element {
  const t = useT();
  const { user, fetchImpl } = useSession();
  const displayRateQ = useDisplayRate();
  const cnyPerUsd = displayRateQ.data?.cnyPerUsd ?? null;
  const canManageBilling = hasPermission(user, 'org:billing:manage');

  return (
    <section data-testid="billing-page" className="flex flex-col gap-4">
      <PageHeader
        title={t('billing.title')}
        sub={t('billing.subtitle')}
        testId="billing-header"
        actions={
          user !== null ? (
            <ExportExcelButton
              testId="billing-export"
              onExport={(onProgress) =>
                exportBilling({
                  fetchImpl,
                  orgId: user.orgId,
                  onProgress,
                })
              }
            />
          ) : null
        }
      />

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <BalancePanel cnyPerUsd={cnyPerUsd} canManageBilling={canManageBilling} />
        {canManageBilling ? <RedeemPanel cnyPerUsd={cnyPerUsd} /> : null}
      </div>

      <CreditsPanel cnyPerUsd={cnyPerUsd} />

      <StatementsPanel cnyPerUsd={cnyPerUsd} />

      {canManageBilling ? <TransactionsPanel cnyPerUsd={cnyPerUsd} /> : null}
    </section>
  );
}

interface BalancePanelProps {
  readonly cnyPerUsd: number | null;
  readonly canManageBilling: boolean;
}

function BalancePanel({ cnyPerUsd, canManageBilling }: BalancePanelProps): JSX.Element {
  const t = useT();
  const balanceQ = useBalance();
  const balance = balanceQ.data;
  const balanceMicros =
    balance !== undefined
      ? usdMicrosFromCentsFallback(balance.balanceUsdMicros, balance.balanceUsdCents)
      : 0;
  const funded = balance !== undefined && balanceMicros > 0;
  // Negative is overdraft, not "needs top-up" (#175): its own chip state.
  const overdrawn = balance !== undefined && balanceMicros < 0;
  const rateHint = formatDisplayRateHint(cnyPerUsd);

  return (
    <Panel
      caption={t('billing.balance.title')}
      padded
      testId="billing-balance-card"
      actions={
        balance !== undefined ? (
          <Chip tone={funded ? 'ok' : overdrawn ? 'bad' : 'warn'} testId="billing-balance-chip">
            {funded
              ? t('billing.balance.status.funded')
              : overdrawn
                ? t('billing.balance.status.overdrawn')
                : t('billing.balance.status.zero')}
          </Chip>
        ) : undefined
      }
    >
      {balanceQ.isLoading ? (
        <Skeleton className="h-24" testId="billing-balance-loading" />
      ) : balanceQ.isError ? (
        <PanelError
          message={t('billing.balance.error')}
          retryLabel={t('billing.retry')}
          onRetry={() => {
            void balanceQ.refetch();
          }}
          testId="billing-balance-error"
        />
      ) : balance !== undefined ? (
        <div className="flex flex-col gap-3">
          <span className="font-mono text-stat text-ink" data-testid="billing-balance-value">
            {formatUsdMicrosOrCentsAsCnyDisplay(
              balance.balanceUsdMicros,
              balance.balanceUsdCents,
              cnyPerUsd,
              overdrawn,
            )}
          </span>
          <GiftBalanceLine balance={balance} cnyPerUsd={cnyPerUsd} testId="billing-balance-gift" />
          <PromoterGrantLine cnyPerUsd={cnyPerUsd} />
          <CreditPanelBody balance={balance} cnyPerUsd={cnyPerUsd} />
          <p className="m-0 font-mono text-mono-label text-ink-low">
            {t('billing.balance.updated', { timestamp: formatTimestamp(balance.updatedAt) })}
            {rateHint === '—' ? '' : ` · ${rateHint}`}
          </p>
          {canManageBilling ? (
            <Link
              to="/billing/topup"
              className={cx(buttonClass('primary'), 'w-fit')}
              data-testid="billing-topup-entry"
            >
              {t('billing.topup.entry')}
            </Link>
          ) : null}
        </div>
      ) : null}
    </Panel>
  );
}

/**
 * The balance, decomposed into the batches it is actually made of.
 *
 * Read with `org:billing:read` — same gate as the balance, because this is
 * the same number seen from closer up, not a movement ledger (that is
 * `TransactionsPanel`, which needs `org:billing:manage`).
 *
 * A 404 renders nothing at all: `/v1/billing/credits` is newer than this
 * page, so a console running against a rolled-back platform must degrade to
 * "no breakdown" rather than to a red error box for a route that simply
 * isn't there yet. Every other failure keeps the normal retry affordance.
 */
function CreditsPanel({ cnyPerUsd }: { readonly cnyPerUsd: number | null }): JSX.Element | null {
  const t = useT();
  const creditsQ = useCredits();

  if (creditsQ.isLoading) {
    return null;
  }
  if (creditsQ.isError) {
    if (creditsQ.error instanceof PlatformApiError && creditsQ.error.status === 404) {
      return null;
    }
    return (
      <Panel caption={t('billing.credits.title')} padded testId="billing-credits-card">
        <PanelError
          message={t('billing.credits.error')}
          retryLabel={t('billing.retry')}
          onRetry={() => {
            void creditsQ.refetch();
          }}
          testId="billing-credits-error"
        />
      </Panel>
    );
  }

  const lots = creditsQ.data?.lots ?? [];
  return (
    <Panel caption={t('billing.credits.title')} padded testId="billing-credits-card">
      <div className="flex flex-col gap-3">
        <p className="m-0 text-caption text-ink-low">{t('billing.credits.subtitle')}</p>
        {lots.length === 0 ? (
          <EmptyState testId="billing-credits-empty" title={t('billing.credits.empty')} />
        ) : (
          <Table testId="billing-credits-table">
            <THead>
              <Tr interactive={false}>
                <Th>{t('billing.credits.column.kind')}</Th>
                <Th num>{t('billing.credits.column.remaining')}</Th>
                <Th num>{t('billing.credits.column.amount')}</Th>
                <Th>{t('billing.credits.column.expires')}</Th>
                <Th>{t('billing.credits.column.issued')}</Th>
              </Tr>
            </THead>
            <TBody>
              {lots.map((lot) => (
                <CreditLotRow key={lot.id} lot={lot} cnyPerUsd={cnyPerUsd} />
              ))}
            </TBody>
          </Table>
        )}
      </div>
    </Panel>
  );
}

function CreditLotRow({
  lot,
  cnyPerUsd,
}: {
  readonly lot: CreditLotEntry;
  readonly cnyPerUsd: number | null;
}): JSX.Element {
  const t = useT();
  const isPromo = lot.kind === 'promo';
  const days = daysUntil(lot.expiresAt);
  return (
    <Tr data-testid={`billing-credit-row-${lot.id}`}>
      <Td label={t('billing.credits.column.kind')}>
        <Chip tone={isPromo ? 'warn' : 'ok'} testId={`billing-credit-kind-${lot.id}`}>
          {isPromo ? t('billing.credits.kind.promo') : t('billing.credits.kind.cash')}
        </Chip>
      </Td>
      <Td num heading label={t('billing.credits.column.remaining')}>
        {formatUsdMicrosOrCentsAsCnyDisplay(
          lot.remainingUsdMicros,
          lot.remainingUsdCents,
          cnyPerUsd,
        )}
      </Td>
      <Td num label={t('billing.credits.column.amount')}>
        {formatUsdMicrosOrCentsAsCnyDisplay(lot.amountUsdMicros, lot.amountUsdCents, cnyPerUsd)}
      </Td>
      <Td
        mono
        label={t('billing.credits.column.expires')}
        data-testid={`billing-credit-expires-${lot.id}`}
      >
        {lot.expiresAt === null ? (
          <span className="text-ink-low">{t('billing.credits.never_expires')}</span>
        ) : (
          // Amber once inside a week: the point of this column is to be
          // noticed before the sweep takes the money back.
          <span className={days !== null && days <= 7 ? 'text-warn' : undefined}>
            {formatTimestamp(lot.expiresAt)}
          </span>
        )}
      </Td>
      <Td mono label={t('billing.credits.column.issued')}>
        {formatTimestamp(lot.issuedAt)}
      </Td>
    </Tr>
  );
}

function RedeemPanel({ cnyPerUsd }: { readonly cnyPerUsd: number | null }): JSX.Element {
  const t = useT();
  const redeemM = useRedeemGiftCard();
  const toast = useToast();
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setError(null);
    setSuccessMessage(null);
    const trimmed = code.trim();
    if (trimmed.length === 0) {
      setError(t('billing.redeem.error.code_required'));
      return;
    }
    try {
      const result = await redeemM.mutateAsync({ code: trimmed });
      setCode('');
      let message: string;
      if (result.kind === 'promoter_code') {
        const bound = t('promoter.redeem.bound');
        if (result.grant.status === 'unconfigured') {
          message = `${bound} · ${t('promoter.redeem.grant_unconfigured')}`;
        } else {
          const amountMicros = result.grant.amountUsdMicros;
          const expiresAt = result.grant.expiresAt;
          if (typeof amountMicros === 'number' && Number.isFinite(amountMicros)) {
            const amount = formatUsdMicrosAsCnyDisplay(amountMicros, cnyPerUsd);
            if (typeof expiresAt === 'string' && expiresAt.length > 0) {
              message = `${bound} · ${t('promoter.redeem.grant_issued', {
                amount,
                date: formatTimestamp(expiresAt),
              })}`;
            } else {
              message = `${bound} · ${t('promoter.grant.included', { amount })}`;
            }
          } else {
            message = `${bound} · ${t('promoter.grant.status_issued')}`;
          }
        }
      } else {
        message = t('billing.redeem.toast.success', {
          amount: formatCnyCents(result.amountCnyCents),
          balance: formatUsdMicrosOrCentsAsCnyDisplay(
            result.newBalanceUsdMicros,
            result.newBalanceUsdCents,
            cnyPerUsd,
          ),
        });
      }
      setSuccessMessage(message);
      toast.success(message);
    } catch (err) {
      const message = localizeApiError(err, t);
      setError(message);
      toast.error(message);
    }
  }

  return (
    <Panel caption={t('billing.redeem.title')} padded testId="billing-redeem-card">
      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          void handleSubmit(e);
        }}
        noValidate
      >
        <FormField
          label={t('billing.redeem.code_label')}
          fieldId="billing-redeem-code"
          error={
            error !== null ? <span data-testid="billing-redeem-error">{error}</span> : undefined
          }
        >
          <Input
            id="billing-redeem-code"
            mono
            value={code}
            onChange={(e) => {
              setCode(e.currentTarget.value);
              setError(null);
            }}
            disabled={redeemM.isPending}
            invalid={error !== null}
            aria-describedby={error !== null ? 'billing-redeem-code-error' : undefined}
            placeholder={t('billing.redeem.code_placeholder')}
            autoComplete="off"
            data-testid="billing-redeem-code"
          />
        </FormField>
        {successMessage !== null ? (
          <p
            className="m-0 rounded-lg border border-ok-line bg-ok-tint px-4 py-2 text-label text-ink"
            role="status"
            data-testid="billing-redeem-toast"
          >
            {successMessage}
          </p>
        ) : null}
        <div className="flex items-center justify-end">
          <Button
            type="submit"
            variant="primary"
            loading={redeemM.isPending}
            data-testid="billing-redeem-submit"
          >
            {redeemM.isPending ? t('billing.redeem.submitting') : t('billing.redeem.submit')}
          </Button>
        </div>
      </form>
      <PromoterBindingStatus />
    </Panel>
  );
}

function PromoterBindingStatus(): JSX.Element | null {
  const t = useT();
  const bindingQ = usePromoterBinding();
  if (bindingQ.isError || bindingQ.data === undefined) {
    return null;
  }
  const { binding } = bindingQ.data;
  if (!binding.bound || binding.boundAt === null || binding.boundAt.length === 0) {
    return null;
  }
  return (
    <p className="m-0 mt-4 text-caption text-ink-low" data-testid="billing-promoter-binding-status">
      {t('promoter.billing.bound', { timestamp: formatTimestamp(binding.boundAt) })}
    </p>
  );
}

function TransactionsPanel({ cnyPerUsd }: { readonly cnyPerUsd: number | null }): JSX.Element {
  const t = useT();
  const [offset, setOffset] = useState(0);
  const txQ = useTransactions({ limit: PAGE_SIZE, offset });
  const balanceQ = useBalance();
  const rows = txQ.data?.transactions ?? [];
  const hasMore = rows.length === PAGE_SIZE;

  // Running balance is only computable on the first page (offset === 0)
  // because we walk backwards from the current balance through each
  // most-recent transaction; deeper pages render "—".
  const runningBalances = computeRunningBalances(
    rows,
    offset === 0 && balanceQ.data !== undefined
      ? usdMicrosFromCentsFallback(balanceQ.data.balanceUsdMicros, balanceQ.data.balanceUsdCents)
      : null,
  );

  return (
    <Panel
      caption={t('billing.transactions.title')}
      testId="billing-transactions-card"
      actions={
        <span
          className="font-mono text-mono-label text-ink-low"
          data-testid="billing-transactions-offset"
        >
          {offset === 0
            ? t('billing.transactions.latest')
            : t('billing.transactions.page', { page: String(Math.floor(offset / PAGE_SIZE) + 1) })}
        </span>
      }
    >
      <div className="flex flex-col gap-3 p-4">
        {txQ.isLoading ? (
          <TableSkeleton rows={4} testId="billing-transactions-loading" />
        ) : txQ.isError ? (
          <PanelError
            message={t('billing.transactions.error')}
            retryLabel={t('billing.retry')}
            onRetry={() => {
              void txQ.refetch();
            }}
            testId="billing-transactions-error"
          />
        ) : rows.length === 0 ? (
          <EmptyState testId="billing-transactions-empty" title={t('billing.transactions.empty')} />
        ) : (
          <Table testId="billing-transactions-table">
            <THead>
              <Tr interactive={false}>
                <Th>{t('billing.transactions.column.date')}</Th>
                <Th>{t('billing.transactions.column.type')}</Th>
                <Th num>{t('billing.transactions.column.amount_usd')}</Th>
                <Th num>{t('billing.transactions.column.running')}</Th>
              </Tr>
            </THead>
            <TBody>
              {rows.map((tx, idx) => (
                <TransactionRow
                  key={tx.id}
                  tx={tx}
                  runningBalanceMicros={runningBalances[idx] ?? null}
                  cnyPerUsd={cnyPerUsd}
                />
              ))}
            </TBody>
          </Table>
        )}

        <div className="flex items-center justify-between">
          <Button
            variant="ghost"
            size="sm"
            disabled={offset === 0 || txQ.isFetching}
            onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
            data-testid="billing-transactions-prev"
          >
            {t('billing.transactions.newer')}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            disabled={!hasMore || txQ.isFetching}
            onClick={() => setOffset(offset + PAGE_SIZE)}
            data-testid="billing-transactions-next"
          >
            {t('billing.transactions.older')}
          </Button>
        </div>
      </div>
    </Panel>
  );
}

interface TransactionRowProps {
  readonly tx: BalanceTransactionEntry;
  readonly runningBalanceMicros: number | null;
  readonly cnyPerUsd: number | null;
}

function TransactionRow({ tx, runningBalanceMicros, cnyPerUsd }: TransactionRowProps): JSX.Element {
  const t = useT();
  const amountUsdMicros = usdMicrosFromCentsFallback(tx.amountUsdMicros, tx.amountUsdCents);
  const isCredit = amountUsdMicros > 0;
  return (
    <Tr data-testid={`billing-tx-row-${tx.id}`}>
      <Td mono heading>
        {formatTimestamp(tx.createdAt)}
      </Td>
      <Td label={t('billing.transactions.column.type')}>
        <Chip tone={isCredit ? 'ok' : 'neutral'} testId={`billing-tx-type-${tx.id}`}>
          {transactionTypeLabel(t, tx.type)}
        </Chip>
      </Td>
      <Td
        num
        label={t('billing.transactions.column.amount_usd')}
        data-testid={`billing-tx-amount-${tx.id}`}
      >
        <span className={isCredit ? 'text-ok' : 'text-bad'}>
          {formatUsdMicrosOrCentsAsCnyMetered(
            tx.amountUsdMicros,
            tx.amountUsdCents,
            cnyPerUsd,
            true,
          )}
        </span>
      </Td>
      <Td
        num
        label={t('billing.transactions.column.running')}
        data-testid={`billing-tx-running-${tx.id}`}
      >
        {runningBalanceMicros !== null
          ? formatUsdMicrosOrCentsAsCnyDisplay(runningBalanceMicros, null, cnyPerUsd)
          : '—'}
      </Td>
    </Tr>
  );
}

function transactionTypeLabel(t: TranslateFn, txType: string): string {
  switch (txType) {
    case 'charge':
      return t('billing.transactions.type.charge');
    case 'gift_card_redeem':
      return t('billing.transactions.type.gift_card_redeem');
    case 'gift_direct_issue':
      return t('billing.transactions.type.gift_direct_issue');
    case 'gift_expiry_reclaim':
      return t('billing.transactions.type.gift_expiry_reclaim');
    case 'adjustment':
      return t('billing.transactions.type.adjustment');
    case 'topup':
      return t('billing.transactions.type.topup');
    case 'external_topup':
      return t('billing.transactions.type.external_topup');
    default:
      return txType.replace(/_/g, ' ');
  }
}

/**
 * Walk the DESC-ordered transaction list and compute the running
 * balance AFTER each row. Row 0 (most-recent) settles into the passed
 * `currentBalanceMicros`; row N's running balance = row N-1's running
 * balance minus row N-1's amount. A null current balance (non-first
 * page, or balance not loaded) yields an all-null array — the UI
 * renders "—".
 */
function computeRunningBalances(
  rows: readonly BalanceTransactionEntry[],
  currentBalanceMicros: number | null,
): readonly (number | null)[] {
  if (currentBalanceMicros === null) {
    return rows.map(() => null);
  }
  const out: number[] = [];
  let runner = currentBalanceMicros;
  for (const tx of rows) {
    out.push(runner);
    runner -= usdMicrosFromCentsFallback(tx.amountUsdMicros, tx.amountUsdCents);
  }
  return out;
}

/**
 * Billing statements (epic #1 PR-N, ported from console-web). Only credit
 * customers accrue statements, so a prepaid-only tenant's
 * `/v1/billing/statements` answers an empty list and this panel renders
 * NOTHING — an empty state would be a permanent dead surface for a tenant who
 * will never have a statement. The loading pass renders nothing for the same
 * reason: we cannot know whether the panel exists until the list is back, and
 * a skeleton that resolves to "no panel" is a layout jump, not information.
 *
 * Consumption is INFORMATIONAL — the amount owed is the closing AR, not gross
 * usage, and the copy keeps the two apart. Overdue is derived client-side from
 * `status` + `dueDate`; the wire never carries an overdue flag.
 */
interface StatementDisplay {
  readonly tone: 'ok' | 'warn' | 'bad';
  readonly labelKey:
    | 'billing.statements.status.settled'
    | 'billing.statements.status.open'
    | 'billing.statements.status.overdue';
}

/**
 * Today as a 'YYYY-MM-DD' calendar date, for comparison against `dueDate`.
 * Both sides are plain date strings, so the compare is a lexicographic one.
 *
 * `dueDate` is a UTC+8 calendar date (see `V1BillingStatement`), so the
 * other side has to be one too. Plain `toISOString()` is UTC, which lags
 * by a day from Beijing midnight to 08:00 — a statement that went
 * overdue overnight would still read "Open" through the whole CN
 * morning. Shifting the instant before slicing puts both sides on the
 * same calendar.
 */
const UTC8_OFFSET_MS = 8 * 60 * 60 * 1000;

function todayYmd(): string {
  return new Date(Date.now() + UTC8_OFFSET_MS).toISOString().slice(0, 10);
}

function deriveStatementDisplay(statement: V1BillingStatement): StatementDisplay {
  if (statement.status === 'closed') {
    return { tone: 'ok', labelKey: 'billing.statements.status.settled' };
  }
  if (statement.dueDate < todayYmd()) {
    return { tone: 'bad', labelKey: 'billing.statements.status.overdue' };
  }
  return { tone: 'warn', labelKey: 'billing.statements.status.open' };
}

function formatStatementPeriod(statement: V1BillingStatement): string {
  return `${statement.periodStart} → ${statement.periodEnd}`;
}

function StatementsPanel({ cnyPerUsd }: { readonly cnyPerUsd: number | null }): JSX.Element | null {
  const t = useT();
  const statementsQ = useStatements();
  const [selected, setSelected] = useState<V1BillingStatement | null>(null);

  if (statementsQ.isLoading) {
    return null;
  }

  const statements = statementsQ.data?.statements ?? [];

  // Only take over the panel when there is nothing else to show. A
  // background refetch that fails leaves `isError` set while the last
  // good statements are still in cache; replacing a readable table with
  // an error box in that case throws away data the user can still use.
  if (statementsQ.isError && statements.length === 0) {
    return (
      <Panel caption={t('billing.statements.title')} testId="billing-statements-card">
        <div className="flex flex-col gap-3 p-4">
          <PanelError
            message={t('billing.statements.error')}
            retryLabel={t('billing.retry')}
            onRetry={() => {
              void statementsQ.refetch();
            }}
            testId="billing-statements-error"
          />
        </div>
      </Panel>
    );
  }

  if (statements.length === 0) {
    return null;
  }

  return (
    <>
      <Panel caption={t('billing.statements.title')} testId="billing-statements-card">
        <div className="flex flex-col gap-3 p-4">
          <p className="m-0 text-caption text-ink-low">{t('billing.statements.subtitle')}</p>
          <Table testId="billing-statements-table">
            <THead>
              <Tr interactive={false}>
                <Th>{t('billing.statements.column.period')}</Th>
                <Th num>{t('billing.statements.column.consumption')}</Th>
                <Th num>{t('billing.statements.column.closing_ar')}</Th>
                <Th>{t('billing.statements.column.due')}</Th>
                <Th>{t('billing.statements.column.status')}</Th>
              </Tr>
            </THead>
            <TBody>
              {statements.map((statement) => (
                <StatementRow
                  key={statement.id}
                  statement={statement}
                  cnyPerUsd={cnyPerUsd}
                  onOpen={() => setSelected(statement)}
                />
              ))}
            </TBody>
          </Table>
        </div>
      </Panel>
      <StatementDetailDialog
        statement={selected}
        cnyPerUsd={cnyPerUsd}
        onClose={() => setSelected(null)}
      />
    </>
  );
}

interface StatementRowProps {
  readonly statement: V1BillingStatement;
  readonly cnyPerUsd: number | null;
  readonly onOpen: () => void;
}

function StatementRow({ statement, cnyPerUsd, onOpen }: StatementRowProps): JSX.Element {
  const t = useT();
  const display = deriveStatementDisplay(statement);
  return (
    <Tr
      data-testid={`billing-statement-row-${statement.id}`}
      className="cursor-pointer"
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onOpen();
        }
      }}
    >
      <Td mono heading label={t('billing.statements.column.period')}>
        {formatStatementPeriod(statement)}
      </Td>
      <Td num label={t('billing.statements.column.consumption')}>
        {formatUsdMicrosOrCentsAsCnyDisplay(statement.consumptionUsdMicros, null, cnyPerUsd)}
      </Td>
      <Td
        num
        label={t('billing.statements.column.closing_ar')}
        data-testid={`billing-statement-ar-${statement.id}`}
      >
        {formatUsdMicrosOrCentsAsCnyDisplay(statement.closingArUsdMicros, null, cnyPerUsd)}
      </Td>
      <Td mono label={t('billing.statements.column.due')}>
        {statement.dueDate}
      </Td>
      <Td label={t('billing.statements.column.status')}>
        <Chip tone={display.tone} testId={`billing-statement-status-${statement.id}`}>
          {t(display.labelKey)}
        </Chip>
      </Td>
    </Tr>
  );
}

interface StatementDetailDialogProps {
  readonly statement: V1BillingStatement | null;
  readonly cnyPerUsd: number | null;
  readonly onClose: () => void;
}

function StatementDetailDialog({
  statement,
  cnyPerUsd,
  onClose,
}: StatementDetailDialogProps): JSX.Element | null {
  const t = useT();
  const generatedId = useId();
  if (statement === null) {
    return null;
  }
  const titleId = `${generatedId}-title`;
  const noteId = `${generatedId}-note`;
  const display = deriveStatementDisplay(statement);
  const rows: readonly {
    readonly label: string;
    readonly value: string;
    readonly testId: string;
  }[] = [
    {
      label: t('billing.statements.detail.period'),
      value: formatStatementPeriod(statement),
      testId: 'billing-statement-detail-period',
    },
    {
      label: t('billing.statements.detail.consumption'),
      value: formatUsdMicrosOrCentsAsCnyDisplay(statement.consumptionUsdMicros, null, cnyPerUsd),
      testId: 'billing-statement-detail-consumption',
    },
    {
      label: t('billing.statements.detail.opening_ar'),
      value: formatUsdMicrosOrCentsAsCnyDisplay(statement.openingArUsdMicros, null, cnyPerUsd),
      testId: 'billing-statement-detail-opening',
    },
    {
      label: t('billing.statements.detail.payments'),
      value: formatUsdMicrosOrCentsAsCnyDisplay(
        statement.paymentsAppliedUsdMicros,
        null,
        cnyPerUsd,
      ),
      testId: 'billing-statement-detail-payments',
    },
    {
      label: t('billing.statements.detail.closing_ar'),
      value: formatUsdMicrosOrCentsAsCnyDisplay(statement.closingArUsdMicros, null, cnyPerUsd),
      testId: 'billing-statement-detail-closing',
    },
    {
      label: t('billing.statements.detail.cny_reference'),
      value:
        statement.cnyReferenceClosingArCents === null
          ? '—'
          : formatCnyCents(statement.cnyReferenceClosingArCents),
      testId: 'billing-statement-detail-cny',
    },
    {
      label: t('billing.statements.detail.due'),
      value: statement.dueDate,
      testId: 'billing-statement-detail-due',
    },
  ];

  return (
    <Modal
      isOpen
      onClose={onClose}
      titleId={titleId}
      descriptionId={noteId}
      testId="billing-statement-detail"
    >
      <div className="flex flex-col gap-5 p-6">
        <div className="flex items-start gap-3">
          <h2 id={titleId} className="m-0 min-w-0 flex-1 text-title text-ink">
            {t('billing.statements.detail.title')}
          </h2>
          <Chip tone={display.tone} testId="billing-statement-detail-status">
            {t(display.labelKey)}
          </Chip>
        </div>

        <dl className="m-0 grid grid-cols-1 gap-3 sm:grid-cols-2">
          {rows.map((row) => (
            <div key={row.testId} className="flex flex-col gap-1">
              <dt className="m-0 text-caption text-ink-low">{row.label}</dt>
              <dd className="m-0 font-mono text-mono text-ink" data-testid={row.testId}>
                {row.value}
              </dd>
            </div>
          ))}
        </dl>

        <p
          id={noteId}
          className="m-0 text-caption text-ink-low"
          data-testid="billing-statement-detail-note"
        >
          {t('billing.statements.detail.consumption_hint')}
        </p>

        <div className="flex items-center justify-end">
          <Button
            variant="secondary"
            onClick={onClose}
            data-testid="billing-statement-detail-close"
          >
            {t('billing.statements.detail.close')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
